// CB:BEGIN pm-channel-dashboard
/**
 * Property Manager Dashboard (/pm-dashboard)
 *
 * Operational multi-property control center — distinct from the homeowner
 * single-project guided experience. Key sections:
 *
 *  - Overview stats (properties, active projects, awaiting bids, completed)
 *  - Active projects table (filterable by property, status, priority)
 *  - Managed properties panel (list + add property)
 *  - New project creation form
 *
 * Requires PROPERTY_MANAGER role; redirects to /pm if user is not a PM.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';
import ContentLimiter from '../components/layout/ContentLimiter';
import SharedHeaderPropertyManager from '../components/SharedHeaderPropertyManager';
import PMSetupModal from '../components/PMSetupModal';
import { useDevice } from '../hooks/useDevice';
import {
  Building2,
  ClipboardList,
  Clock,
  CheckCircle2,
  Plus,
  AlertTriangle,
  ChevronDown,
  DollarSign,
  TrendingUp,
} from 'lucide-react';
import MetricCard from '../components/ui/MetricCard';
import StatusBadge from '../components/ui/StatusBadge';

const BRAND_GREEN = '#123c3b';

interface PropertyManagerDashboardProps {
  onNavigate: (screen: string) => void;
}

interface PMProfile {
  id: string;
  companyName?: string;
  contactName?: string;
  portfolioSize?: number;
}

interface Property {
  id: string;
  label?: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  propertyType: string;
  _count?: { projects: number };
}

interface Project {
  id: string;
  address: string;
  city?: string;
  state?: string;
  projectType: string;
  status: string;
  priority: string;
  requestorType: string;
  requestedResponseBy?: string;
  createdAt: string;
  property?: { id: string; label?: string; address1: string; city: string; state: string };
  bids?: { id: string; amount: number; isAccepted: boolean }[];
}

// Status badge colors
const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  DRAFT: { bg: '#fef3c7', color: '#92400e' },
  POSTED: { bg: '#dbeafe', color: '#1d4ed8' },
  BIDS_COLLECTING: { bg: '#fef9c3', color: '#854d0e' },
  BIDS_READY: { bg: '#dcfce7', color: '#166534' },
  DECIDING: { bg: '#fef9c3', color: '#854d0e' },
  IN_PROGRESS: { bg: '#e0f2fe', color: '#0369a1' },
  ACTIVE_P1: { bg: '#e0f2fe', color: '#0369a1' },
  ACTIVE_P2: { bg: '#e0f2fe', color: '#0369a1' },
  ACTIVE_P3: { bg: '#e0f2fe', color: '#0369a1' },
  COMPLETED: { bg: '#dcfce7', color: '#166534' },
  CANCELLED: { bg: '#fee2e2', color: '#dc2626' },
};

const PRIORITY_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  EMERGENCY: { bg: '#fee2e2', color: '#dc2626', label: 'Emergency' },
  HIGH: { bg: '#fef3c7', color: '#b45309', label: 'High' },
  NORMAL: { bg: '#f1f5f9', color: '#475569', label: 'Normal' },
};

const PROJECT_TYPES = ['ROOF', 'SIDING', 'GUTTERS', 'WINDOWS', 'DOORS', 'OTHER'];
const PROPERTY_TYPES = ['RESIDENTIAL', 'COMMERCIAL', 'MIXED_USE'];

function formatCurrency(cents: number): string {
  if (cents === 0) return '$0';
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}k`;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    DRAFT: 'Draft — Action Required',
    POSTED: 'Posted',
    BIDS_COLLECTING: 'Collecting Bids',
    BIDS_READY: 'Bids Ready',
    DECIDING: 'Deciding',
    BID_SELECTED: 'Bid Selected',
    IN_PROGRESS: 'In Progress',
    ACTIVE_P1: 'Active (P1)',
    ACTIVE_P2: 'Active (P2)',
    ACTIVE_P3: 'Active (P3)',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
  };
  return map[status] || status;
}

// ---------------------------------------------------------------------------
// Add Property Modal
// ---------------------------------------------------------------------------
function AddPropertyModal({
  onClose,
  onSaved,
  token,
}: {
  onClose: () => void;
  onSaved: () => void;
  token: string | null;
}) {
  const [form, setForm] = useState({
    label: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    propertyType: 'RESIDENTIAL',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Address autocomplete
  const [propSuggestions, setPropSuggestions] = useState<any[]>([]);
  const [showPropSuggestions, setShowPropSuggestions] = useState(false);
  const [propSuggestionIndex, setPropSuggestionIndex] = useState(-1);
  const ignorePropPredictions = useRef(false);
  const propAddressRef = useRef<HTMLInputElement>(null);

  const fetchPropSuggestions = async (input: string) => {
    if (input.length < 3) { setPropSuggestions([]); setShowPropSuggestions(false); return; }
    try {
      const res = await fetch(getApiUrl(`/api/autocomplete?input=${encodeURIComponent(input)}`));
      if (res.ok) {
        const data = await res.json();
        if (data.predictions?.length) {
          setPropSuggestions(data.predictions);
          setShowPropSuggestions(true);
          setPropSuggestionIndex(-1);
        } else {
          setPropSuggestions([]); setShowPropSuggestions(false);
        }
      }
    } catch { /* silent */ }
  };

  const handlePropSuggestionSelect = async (suggestion: any) => {
    const full: string = suggestion.description || '';
    const street = full.replace(/, USA$/, '').split(',')[0].trim();
    ignorePropPredictions.current = true;
    setForm(f => ({ ...f, address1: street }));
    setPropSuggestions([]);
    setShowPropSuggestions(false);
    if (suggestion.place_id) {
      try {
        const res = await fetch(getApiUrl(`/api/details?place_id=${encodeURIComponent(suggestion.place_id)}`));
        if (res.ok) {
          const data = await res.json();
          const components: any[] = data.result?.address_components || [];
          const get = (type: string) => components.find((c: any) => c.types.includes(type));
          const streetNum = get('street_number')?.long_name || '';
          const route = get('route')?.short_name || '';
          const city = get('locality')?.long_name || get('sublocality')?.long_name || '';
          const state = get('administrative_area_level_1')?.short_name || '';
          const zip = get('postal_code')?.long_name || '';
          const address1 = [streetNum, route].filter(Boolean).join(' ') || street;
          setForm(f => ({ ...f, address1, city, state, zip }));
        }
      } catch { /* keep optimistic */ }
    }
    setTimeout(() => propAddressRef.current?.focus(), 0);
  };

  const handlePropAddressKeyDown = (e: React.KeyboardEvent) => {
    if (!showPropSuggestions) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setPropSuggestionIndex(i => Math.min(i + 1, propSuggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setPropSuggestionIndex(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && propSuggestionIndex >= 0) { e.preventDefault(); handlePropSuggestionSelect(propSuggestions[propSuggestionIndex]); }
    else if (e.key === 'Escape') { setShowPropSuggestions(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const resp = await fetch(getApiUrl('/api/property-manager/properties'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setError(d.error || 'Failed to save property');
        return;
      }
      onSaved();
      onClose();
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          width: '100%',
          maxWidth: 540,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>Add Property</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#94a3b8',
            }}
          >
            ×
          </button>
        </div>
        {error && (
          <div
            style={{
              background: '#fee2e2',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {/* Property Nickname */}
          {[
            { label: 'Property Nickname (optional)', key: 'label', required: false, placeholder: 'e.g. Downtown Office, Unit 4B' },
          ].map((f) => (
            <div key={f.key} style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{f.label}</label>
              <input
                type="text"
                required={f.required}
                placeholder={f.placeholder}
                value={(form as any)[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
          ))}

          {/* Street Address — with autocomplete */}
          <div style={{ marginBottom: 16, position: 'relative' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Street Address</label>
            <input
              ref={propAddressRef}
              type="text"
              required
              placeholder="123 Main St"
              value={form.address1}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-lpignore="true"
              onChange={(e) => {
                const val = e.target.value;
                setForm(f => ({ ...f, address1: val }));
                if (ignorePropPredictions.current) { ignorePropPredictions.current = false; return; }
                fetchPropSuggestions(val);
              }}
              onKeyDown={handlePropAddressKeyDown}
              onBlur={() => setTimeout(() => setShowPropSuggestions(false), 150)}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
            />
            {showPropSuggestions && propSuggestions.length > 0 && (
              <ul style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
                background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                marginTop: 2, padding: 0, listStyle: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                maxHeight: 220, overflowY: 'auto',
              }}>
                {propSuggestions.map((s, idx) => (
                  <li
                    key={s.place_id || idx}
                    onMouseDown={() => handlePropSuggestionSelect(s)}
                    style={{
                      padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                      background: idx === propSuggestionIndex ? '#f0fdf4' : '#fff',
                      color: '#111827',
                      borderBottom: idx < propSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                    }}
                  >
                    {(s.description || '').replace(/, USA$/, '')}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Suite / Unit */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Suite / Unit (optional)</label>
            <input
              type="text"
              placeholder="Apt 2B"
              value={form.address2}
              onChange={(e) => setForm({ ...form, address2: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
            />
          </div>

          {/* City / State / Zip — auto-filled by autocomplete, still editable */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>City</label>
              <input required placeholder="Nashville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>State</label>
              <input required placeholder="TN" maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>ZIP</label>
              <input required placeholder="37201" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Property Type</label>
            <select
              value={form.propertyType}
              onChange={(e) => setForm({ ...form, propertyType: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' }}
            >
              {PROPERTY_TYPES.map((t) => (
                <option key={t} value={t}>{t.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Notes (optional)</label>
            <textarea
              placeholder="Any additional information — access instructions, tenant contacts, HOA rules, insurance details, etc."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            style={{
              width: '100%',
              padding: '12px',
              background: BRAND_GREEN,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Add Property'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Project Modal
// ---------------------------------------------------------------------------
// Property Import Modal (CSV / Excel)
// ---------------------------------------------------------------------------
function ImportPropertiesModal({
  onClose,
  onImported,
  token,
}: {
  onClose: () => void;
  onImported: () => void;
  token: string | null;
}) {
  type PreviewRow = { rowNum: number; label: string | null; address1: string; city: string; state: string; zip: string; propertyType: string; isDuplicate?: boolean; warnings?: string[]; errors?: string[] };
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [preview, setPreview] = useState<{ validRows: PreviewRow[]; errorRows: any[]; total: number } | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; failed: number } | null>(null);
  const [error, setError] = useState('');

  const parseFile = async (file: File) => {
    setFileName(file.name);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(getApiUrl('/api/pm/v3/import/parse-file'), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to parse file'); return; }
      setRows(data.rows);
      validateRows(data.rows);
    } catch { setError('Network error during file parse'); }
  };

  const validateRows = async (parsedRows: Record<string, string>[]) => {
    setError('');
    try {
      const res = await fetch(getApiUrl('/api/pm/v3/import/validate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ rows: parsedRows }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Validation failed'); return; }
      setPreview(data);
      setStep('preview');
    } catch { setError('Network error during validation'); }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setImporting(true);
    setError('');
    try {
      const res = await fetch(getApiUrl('/api/pm/v3/import/confirm'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ rows: preview.validRows, fileName }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Import failed'); return; }
      setResult({ imported: data.imported, failed: data.failed || 0 });
      setStep('done');
      onImported();
    } catch { setError('Network error during import'); }
    finally { setImporting(false); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 580, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>Import Properties</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}

        {step === 'upload' && (
          <div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              Upload a CSV or Excel file (.csv, .xlsx). Required columns: <strong>address, city, state, zip</strong>. Optional: name, propertyType, unitCount, occupancyStatus, accessNotes.
            </p>
            <div style={{ marginBottom: 16 }}>
              <a
                href={getApiUrl('/api/pm/v3/import/template')}
                style={{ fontSize: 13, color: '#2563eb', textDecoration: 'underline' }}
              >
                Download template CSV
              </a>
            </div>
            <label style={{ display: 'block', border: '2px dashed #d1d5db', borderRadius: 10, padding: '32px 16px', textAlign: 'center', cursor: 'pointer', background: '#f9fafb' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Click to choose file</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>CSV or Excel (.csv, .xlsx)</div>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) parseFile(f); }}
              />
            </label>
          </div>
        )}

        {step === 'preview' && preview && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Total rows', value: preview.total, color: '#374151' },
                { label: 'Valid', value: preview.validRows.length, color: '#059669' },
                { label: 'Errors', value: preview.errorRows.length, color: preview.errorRows.length > 0 ? '#dc2626' : '#94a3b8' },
              ].map(s => (
                <div key={s.label} style={{ flex: 1, background: '#f9fafb', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {preview.errorRows.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>ROWS WITH ERRORS (will be skipped)</div>
                {preview.errorRows.slice(0, 5).map((r: any) => (
                  <div key={r.rowNum} style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', padding: '6px 10px', borderRadius: 6, marginBottom: 4 }}>
                    Row {r.rowNum}: {r.errors?.join(', ')}
                  </div>
                ))}
                {preview.errorRows.length > 5 && <div style={{ fontSize: 12, color: '#94a3b8' }}>…and {preview.errorRows.length - 5} more</div>}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>PREVIEW (first 5 valid rows)</div>
              {preview.validRows.slice(0, 5).map((r) => (
                <div key={r.rowNum} style={{ fontSize: 12, padding: '6px 10px', background: r.isDuplicate ? '#fffbeb' : '#f0fdf4', borderRadius: 6, marginBottom: 4 }}>
                  <strong>{r.address1}</strong>, {r.city}, {r.state} {r.zip}
                  {r.label && <span style={{ color: '#64748b' }}> — {r.label}</span>}
                  {r.isDuplicate && <span style={{ color: '#d97706', marginLeft: 6 }}>⚠ possible duplicate</span>}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep('upload')} style={{ flex: 1, padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, background: '#f9fafb', fontSize: 14, cursor: 'pointer' }}>Back</button>
              <button
                onClick={handleConfirm}
                disabled={importing || preview.validRows.length === 0}
                style={{ flex: 2, padding: '10px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: importing || preview.validRows.length === 0 ? 'not-allowed' : 'pointer', opacity: importing || preview.validRows.length === 0 ? 0.6 : 1 }}
              >
                {importing ? 'Importing…' : `Import ${preview.validRows.length} Propert${preview.validRows.length === 1 ? 'y' : 'ies'}`}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Import complete</div>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
              {result.imported} propert{result.imported === 1 ? 'y' : 'ies'} added{result.failed > 0 ? `, ${result.failed} skipped` : ''}.
            </div>
            <button onClick={onClose} style={{ padding: '10px 28px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function AddProjectModal({
  properties,
  onClose,
  onSaved,
  token,
}: {
  properties: Property[];
  onClose: () => void;
  onSaved: () => void;
  token: string | null;
}) {
  // CB:BEGIN pm-project-first-flow
  // Supports both property-first (select existing) and project-first (create inline).
  const [propertyMode, setPropertyMode] = useState<'select' | 'create'>(
    properties.length > 0 ? 'select' : 'create'
  );
  const [form, setForm] = useState({
    propertyId: properties[0]?.id || '',
    projectType: 'ROOF',
    extraInfo: '',
    priority: 'NORMAL',
    requestedResponseBy: (() => { const d = new Date(); d.setDate(d.getDate() + 2); return d.toISOString().slice(0, 10); })(),
    requestedSiteVisitBy: (() => { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10); })(),
    pmNotes: '',
  });
  const [inlineProperty, setInlineProperty] = useState({
    label: '',
    address1: '',
    city: '',
    state: '',
    zip: '',
    propertyType: 'RESIDENTIAL',
  });
  // CB:END pm-project-first-flow

  // Address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressSuggestionIndex, setAddressSuggestionIndex] = useState(-1);
  const ignoreAddressPredictions = useRef(false);
  const addressInputRef = useRef<HTMLInputElement>(null);

  const fetchAddressSuggestions = async (input: string) => {
    if (input.length < 3) { setAddressSuggestions([]); setShowAddressSuggestions(false); return; }
    try {
      const res = await fetch(getApiUrl(`/api/autocomplete?input=${encodeURIComponent(input)}`));
      if (res.ok) {
        const data = await res.json();
        if (data.predictions?.length) {
          setAddressSuggestions(data.predictions);
          setShowAddressSuggestions(true);
          setAddressSuggestionIndex(-1);
        } else {
          setAddressSuggestions([]); setShowAddressSuggestions(false);
        }
      }
    } catch { /* silent */ }
  };

  const handleAddressSuggestionSelect = async (suggestion: any) => {
    const full: string = suggestion.description || '';
    // Optimistically set address1 from description while details load
    const descParts = full.replace(/, USA$/, '').split(',').map((s: string) => s.trim());
    const street = descParts[0] || full;
    ignoreAddressPredictions.current = true;
    setInlineProperty(p => ({ ...p, address1: street }));
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);

    // Fetch place details to get accurate address_components (street, city, state, zip)
    if (suggestion.place_id) {
      try {
        const res = await fetch(getApiUrl(`/api/details?place_id=${encodeURIComponent(suggestion.place_id)}`));
        if (res.ok) {
          const data = await res.json();
          const components: any[] = data.result?.address_components || [];
          const get = (type: string) => components.find((c: any) => c.types.includes(type));
          const streetNum = get('street_number')?.long_name || '';
          const route = get('route')?.short_name || '';
          const city = get('locality')?.long_name || get('sublocality')?.long_name || get('neighborhood')?.long_name || descParts[1] || '';
          const state = get('administrative_area_level_1')?.short_name || '';
          const zip = get('postal_code')?.long_name || '';
          const address1 = [streetNum, route].filter(Boolean).join(' ') || street;
          setInlineProperty(p => ({ ...p, address1, city, state, zip }));
        }
      } catch { /* keep optimistic values */ }
    }

    setTimeout(() => { addressInputRef.current?.focus(); }, 0);
  };

  const handleAddressKeyDown = (e: React.KeyboardEvent) => {
    if (!showAddressSuggestions) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setAddressSuggestionIndex(i => Math.min(i + 1, addressSuggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAddressSuggestionIndex(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && addressSuggestionIndex >= 0) { e.preventDefault(); handleAddressSuggestionSelect(addressSuggestions[addressSuggestionIndex]); }
    else if (e.key === 'Escape') { setShowAddressSuggestions(false); }
  };

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (propertyMode === 'select' && !form.propertyId) {
      setError('Please select a property or create a new one.');
      return;
    }
    if (propertyMode === 'create') {
      if (!inlineProperty.address1.trim() || !inlineProperty.city.trim() || !inlineProperty.state.trim() || !inlineProperty.zip.trim()) {
        setError('Property address, city, state, and ZIP are required.');
        return;
      }
    }
    setSaving(true);
    setError('');
    try {
      const body: Record<string, any> = {
        projectType: form.projectType,
        priority: form.priority,
        extraInfo: form.extraInfo || undefined,
        requestedResponseBy: form.requestedResponseBy || undefined,
        requestedSiteVisitBy: form.requestedSiteVisitBy || undefined,
        pmNotes: form.pmNotes || undefined,
      };
      if (propertyMode === 'select') {
        body.propertyId = form.propertyId;
      } else {
        body.inlineProperty = {
          address1: inlineProperty.address1.trim(),
          city: inlineProperty.city.trim(),
          state: inlineProperty.state.trim().toUpperCase(),
          zip: inlineProperty.zip.trim(),
          label: inlineProperty.label.trim() || undefined,
          propertyType: inlineProperty.propertyType,
        };
      }
      const resp = await fetch(getApiUrl('/api/property-manager/projects'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setError(d.error || 'Failed to create project');
        return;
      }
      onSaved();
      onClose();
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          width: '100%',
          maxWidth: 560,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          margin: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>New Project</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#94a3b8',
            }}
          >
            ×
          </button>
        </div>
        {error && (
          <div
            style={{
              background: '#fee2e2',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {/* CB:BEGIN pm-property-selector */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Property</label>
              {properties.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPropertyMode(m => m === 'select' ? 'create' : 'select')}
                  style={{ background: 'none', border: 'none', fontSize: 12, color: BRAND_GREEN, cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}
                >
                  {propertyMode === 'select' ? '+ Create new property' : '← Use existing property'}
                </button>
              )}
            </div>
            {propertyMode === 'select' ? (
              <select
                value={form.propertyId}
                onChange={(e) => setForm({ ...form, propertyId: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' }}
              >
                <option value="">Select a property...</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.label ? `${p.label} — ` : ''}{p.address1}, {p.city}, {p.state}</option>
                ))}
              </select>
            ) : (
              <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', background: '#f8fafc' }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, fontWeight: 600 }}>NEW PROPERTY</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ gridColumn: '1/-1' }}>
                    <input placeholder="Property label (optional)" value={inlineProperty.label} onChange={e => setInlineProperty(p => ({ ...p, label: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ gridColumn: '1/-1', position: 'relative' }}>
                    <input
                      ref={addressInputRef}
                      required
                      placeholder="Street address *"
                      value={inlineProperty.address1}
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      data-lpignore="true"
                      onChange={e => {
                        const val = e.target.value;
                        setInlineProperty(p => ({ ...p, address1: val }));
                        if (ignoreAddressPredictions.current) { ignoreAddressPredictions.current = false; return; }
                        fetchAddressSuggestions(val);
                      }}
                      onKeyDown={handleAddressKeyDown}
                      onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 150)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                    />
                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <ul style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
                        background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                        marginTop: 2, padding: 0, listStyle: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        maxHeight: 220, overflowY: 'auto',
                      }}>
                        {addressSuggestions.map((s, idx) => (
                          <li
                            key={s.place_id || idx}
                            onMouseDown={() => handleAddressSuggestionSelect(s)}
                            style={{
                              padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                              background: idx === addressSuggestionIndex ? '#f0fdf4' : '#fff',
                              color: '#111827',
                              borderBottom: idx < addressSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                            }}
                          >
                            {(s.description || '').replace(/, USA$/, '')}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <input placeholder="City" value={inlineProperty.city} onChange={e => setInlineProperty(p => ({ ...p, city: e.target.value }))} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <input placeholder="State" maxLength={2} value={inlineProperty.state} onChange={e => setInlineProperty(p => ({ ...p, state: e.target.value }))} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                    <input placeholder="ZIP" value={inlineProperty.zip} onChange={e => setInlineProperty(p => ({ ...p, zip: e.target.value }))} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <select value={inlineProperty.propertyType} onChange={e => setInlineProperty(p => ({ ...p, propertyType: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                      {[['RESIDENTIAL','Residential'],['COMMERCIAL','Commercial'],['MULTIFAMILY','Multi-Family'],['HOA','HOA / Community'],['OTHER','Other']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* CB:END pm-property-selector */}

          <div style={{ marginBottom: 16 }}>
            <label
              style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}
            >
              Work Type
            </label>
            <select
              value={form.projectType}
              onChange={(e) => setForm({ ...form, projectType: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                fontSize: 14,
                background: '#fff',
              }}
            >
              <option value="ROOF">Roof</option>
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label
              style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}
            >
              Priority
            </label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                fontSize: 14,
                background: '#fff',
              }}
            >
              <option value="NORMAL">Normal</option>
              <option value="HIGH">High</option>
              <option value="EMERGENCY">Emergency</option>
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label
              style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}
            >
              Scope / Description
            </label>
            <textarea
              rows={3}
              placeholder="Describe the issue and scope of work needed..."
              value={form.extraInfo}
              onChange={(e) => setForm({ ...form, extraInfo: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                fontSize: 14,
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label
                style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}
              >
                Bid Response Deadline
              </label>
              <input
                type="date"
                value={form.requestedResponseBy}
                onChange={(e) => setForm({ ...form, requestedResponseBy: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Visible to contractors</div>
            </div>
            <div>
              <label
                style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}
              >
                Site Visit by
              </label>
              <input
                type="date"
                value={form.requestedSiteVisitBy}
                onChange={(e) => setForm({ ...form, requestedSiteVisitBy: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label
              style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}
            >
              Internal Notes (not shown to contractors)
            </label>
            <textarea
              rows={2}
              placeholder="Tenant contact info, access instructions, internal reference numbers..."
              value={form.pmNotes}
              onChange={(e) => setForm({ ...form, pmNotes: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                fontSize: 14,
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            style={{
              width: '100%',
              padding: '12px',
              background: BRAND_GREEN,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Creating...' : 'Save Project Draft'}
          </button>
          <p style={{ marginTop: 10, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            Project starts as a draft. You'll sign to activate it before contractors are notified.
          </p>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Acknowledge Project Modal
// CB:BEGIN pm-project-ack-modal
// PM must review and sign the project-level acknowledgment before the project
// goes live to contractors. Records typed name in metadata, transitions DRAFT→POSTED.
// ---------------------------------------------------------------------------
function AcknowledgeProjectModal({
  project,
  onClose,
  onAcknowledged,
  token,
}: {
  project: Project;
  onClose: () => void;
  onAcknowledged: () => void;
  token: string | null;
}) {
  const [typedName, setTypedName] = useState('');
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const propertyLabel = project.property?.label || project.property?.address1 || project.address;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!typedName.trim()) { setError('Please type your full legal name to sign.'); return; }
    if (!checked) { setError('Please check the acknowledgment box to continue.'); return; }
    setSaving(true);
    setError('');
    try {
      const resp = await fetch(getApiUrl(`/api/property-manager/projects/${project.id}/acknowledge`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ typedName: typedName.trim() }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setError(d.error || 'Failed to acknowledge project');
        return;
      }
      onAcknowledged();
      onClose();
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', margin: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0, marginBottom: 4 }}>Activate Project</h2>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{propertyLabel} — {project.projectType.charAt(0) + project.projectType.slice(1).toLowerCase()}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
          <strong style={{ color: '#0f172a' }}>Project Acknowledgment</strong><br />
          By activating this project, I confirm that: (1) I have authority to request contractor bids on this property; (2) the project information submitted is accurate and complete; (3) CoreBids is facilitating bid collection only and is not the contractor performing the work; (4) contractor response times and pricing are not guaranteed by CoreBids; and (5) I agree to fund the agreed project amount through CoreBids escrow upon contractor selection.
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Type your full legal name to sign <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
              placeholder="Full legal name"
              value={typedName}
              onChange={e => setTypedName(e.target.value)}
              autoFocus
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 24 }}>
            <input id="pm-proj-ack-check" type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="pm-proj-ack-check" style={{ fontSize: 13, color: '#1f2937', cursor: 'pointer', lineHeight: 1.5 }}>
              I have read and agree to the project acknowledgment above. I understand CoreBids is coordinating this project and is not the contractor performing the work. <span style={{ color: '#dc2626' }}>*</span>
            </label>
          </div>
          <button
            type="submit"
            disabled={saving}
            style={{ width: '100%', padding: '12px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Activating…' : 'Activate Project — Go Live'}
          </button>
          <p style={{ marginTop: 12, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            CoreBids facilitates contractor bid collection. Contractors in your area will be notified.
          </p>
        </form>
      </div>
    </div>
  );
}
// CB:END pm-project-ack-modal

// ---------------------------------------------------------------------------
// Property Detail Modal
// ---------------------------------------------------------------------------
function PropertyDetailModal({ property, onClose }: { property: Property; onClose: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>
              {property.label || property.address1}
            </h2>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {property.address1}, {property.city}, {property.state} {property.zip}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', marginBottom: 4 }}>TYPE</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{property.propertyType.replace(/_/g, ' ')}</div>
          </div>
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', marginBottom: 4 }}>PROJECTS</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{property._count?.projects ?? 0}</div>
          </div>
        </div>

        {(property as any).notes && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', marginBottom: 6 }}>NOTES</div>
            <div style={{ fontSize: 13, color: '#374151', background: '#f8fafc', borderRadius: 8, padding: '10px 14px', lineHeight: 1.6 }}>
              {(property as any).notes}
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          style={{ width: '100%', padding: '11px', background: '#f1f5f9', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer' }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Properties list with "View All" toggle (shows 3 by default)
// ---------------------------------------------------------------------------
const PROPERTIES_PREVIEW = 3;

function PmPropertiesList({ properties, onNavigate }: { properties: Property[]; onNavigate?: (screen: string) => void }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? properties : properties.slice(0, PROPERTIES_PREVIEW);

  return (
    <>
      <div>
        {visible.map((prop) => (
          <button
            key={prop.id}
            onClick={() => onNavigate ? onNavigate(`/pm/properties/${prop.id}`) : undefined}
            style={{
              width: '100%', padding: '16px 24px',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
              background: 'none', border: 'none', borderBottom: '1px solid #f8fafc', cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {prop.label && (
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {prop.label}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {prop.address1}, {prop.city}, {prop.state} {prop.zip}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                {prop.propertyType.replace(/_/g, ' ')} &middot; {prop._count?.projects ?? 0} project{(prop._count?.projects ?? 0) !== 1 ? 's' : ''}
              </div>
            </div>
            <span style={{ fontSize: 16, color: '#cbd5e1', flexShrink: 0, marginTop: 2 }}>›</span>
          </button>
        ))}
        {properties.length > PROPERTIES_PREVIEW && (
          <button
            onClick={() => onNavigate ? onNavigate('pm-properties') : setShowAll(v => !v)}
            style={{ width: '100%', padding: '12px', background: 'none', border: 'none', borderTop: '1px solid #f1f5f9', fontSize: 13, fontWeight: 600, color: '#1a6b4a', cursor: 'pointer' }}
          >
            {`View all ${properties.length} properties →`}
          </button>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Dashboard Component
// ---------------------------------------------------------------------------
const PropertyManagerDashboard: React.FC<PropertyManagerDashboardProps> = ({ onNavigate }) => {
  const { isMobileLayout } = useDevice();
  const { getToken } = useAuth();
  const { user, isSignedIn } = useUser();

  const [profile, setProfile] = useState<PMProfile | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showImportProperties, setShowImportProperties] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [acknowledgeProject, setAcknowledgeProject] = useState<Project | null>(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPropertyId, setFilterPropertyId] = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  const [token, setToken] = useState<string | null>(null);

  // Resolve token once
  useEffect(() => {
    if (!isSignedIn) return;
    getToken().then(setToken).catch(() => {});
  }, [isSignedIn, getToken]);

  // Redirect if not PM
  useEffect(() => {
    if (!isSignedIn) {
      onNavigate('pm-landing');
      return;
    }
    const meta = (user?.publicMetadata as any) || {};
    const usertype = (meta.usertype || meta.userType || '').toString().toLowerCase();
    const cached = localStorage.getItem('cb.usertype');
    if (usertype && usertype !== 'property_manager') {
      onNavigate('landing');
    } else if (!usertype && cached && cached !== 'property_manager') {
      onNavigate('landing');
    }
  }, [isSignedIn, user, onNavigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Always get a fresh token — avoids 401s caused by stale state during initial render
      const freshToken = await getToken().catch(() => null);
      if (!freshToken) { setLoading(false); return; }
      setToken(freshToken);

      const headers: Record<string, string> = {
        Authorization: `Bearer ${freshToken}`,
        'Content-Type': 'application/json',
      };

      const [profileRes, propertiesRes, projectsRes, agreementRes] = await Promise.all([
        fetch(getApiUrl('/api/property-manager/me'), { headers, credentials: 'include' }),
        fetch(getApiUrl('/api/property-manager/properties'), { headers, credentials: 'include' }),
        fetch(getApiUrl('/api/property-manager/projects'), { headers, credentials: 'include' }),
        fetch(getApiUrl('/api/compliance/pm-agreement-status'), { headers, credentials: 'include' }),
      ]);

      // Agreement status fetched but not displayed — gate enforcement happens server-side

      if (profileRes.ok) {
        const d = await profileRes.json();
        setProfile(d.profile);
      }
      if (propertiesRes.ok) {
        const d = await propertiesRes.json();
        setProperties(d.properties || []);
      }
      if (projectsRes.ok) {
        const d = await projectsRes.json();
        setProjects(d.projects || []);
      }
    } catch {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (isSignedIn) fetchData();
  }, [isSignedIn, fetchData]);

  // Derived stats
  const activeProjects = projects.filter((p) =>
    ['POSTED', 'BIDS_COLLECTING', 'BIDS_READY', 'DECIDING', 'IN_PROGRESS', 'ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3'].includes(p.status)
  );
  const awaitingBids = projects.filter((p) =>
    ['POSTED', 'BIDS_COLLECTING'].includes(p.status)
  );
  const completedThisMonth = projects.filter((p) => {
    if (p.status !== 'COMPLETED') return false;
    const now = new Date();
    const d = new Date(p.createdAt);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  });

  // Spend stats derived from accepted bids (bid.amount is stored in cents)
  const ACTIVE_STATUSES = ['POSTED', 'BIDS_COLLECTING', 'BIDS_READY', 'DECIDING', 'BID_SELECTED', 'HOMEOWNER_CONTRACT_SIGNING', 'ESCROW_PENDING', 'IN_PROGRESS', 'ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3'];
  const committedSpend = projects
    .filter(p => ACTIVE_STATUSES.includes(p.status))
    .flatMap(p => p.bids || [])
    .filter(b => b.isAccepted)
    .reduce((sum, b) => sum + (b.amount || 0), 0);
  const completedSpend = projects
    .filter(p => p.status === 'COMPLETED')
    .flatMap(p => p.bids || [])
    .filter(b => b.isAccepted)
    .reduce((sum, b) => sum + (b.amount || 0), 0);

  // CB:BEGIN pm-channel-needs-attention
  const now = new Date();
  const needsAttention = projects.filter((p) => {
    // DRAFT projects require PM signature — always surfaces as action required
    if (p.status === 'DRAFT') return true;
    // Overdue response deadline
    if (p.requestedResponseBy && new Date(p.requestedResponseBy) < now &&
        ['POSTED', 'BIDS_COLLECTING'].includes(p.status)) return true;
    // Awaiting bids for >3 days with no bids
    if (['POSTED', 'BIDS_COLLECTING'].includes(p.status) && (!p.bids || p.bids.length === 0)) {
      const created = new Date(p.createdAt);
      const ageMs = now.getTime() - created.getTime();
      if (ageMs > 3 * 24 * 60 * 60 * 1000) return true;
    }
    return false;
  });
  // CB:END pm-channel-needs-attention

  // Filtered projects
  const filteredProjects = projects.filter((p) => {
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterPropertyId && p.property?.id !== filterPropertyId) return false;
    if (filterPriority && p.priority !== filterPriority) return false;
    return true;
  });

  // Name to display — prefer DB company name; never fall back to raw email
  const displayName = profile?.companyName || profile?.contactName || 'CoreBids PM';

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#64748b',
          fontSize: 16,
        }}
      >
        Loading dashboard...
      </div>
    );
  }

  // CB:BEGIN pm-setup-modal-trigger
  // Read signup pre-fill data stored by PropertyManagerSignup
  const signupData = (() => {
    try { return JSON.parse(localStorage.getItem('cb.pm.signup') || '{}'); } catch { return {}; }
  })();
  const defaultCompanyName = signupData.companyName || user?.firstName || '';
  const defaultPhone = signupData.phone || '';
  const defaultCompanyAddress = signupData.companyAddress || '';
  // CB:END pm-setup-modal-trigger

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f1f5f9',
        fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif',
      }}
    >
      {/* Blocking setup modal — shown when PM profile is missing or incomplete (no companyName) */}
      {(!profile || !profile.companyName) && (
        <PMSetupModal
          onComplete={fetchData}
          defaultCompanyName={defaultCompanyName}
          defaultPhone={defaultPhone}
          defaultCompanyAddress={defaultCompanyAddress}
        />
      )}
      <SharedHeaderPropertyManager
        onNavigate={onNavigate}
        activeScreen="pm-dashboard"
        companyName={displayName}
      />

      <main style={{ padding: isMobileLayout ? '24px 16px' : '40px 24px' }}>
        <ContentLimiter>
          {/* Page title */}
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
              {displayName}
            </h1>
            <p style={{ fontSize: 15, color: '#64748b' }}>
              {properties.length} {properties.length === 1 ? 'property' : 'properties'} in portfolio
            </p>
          </div>

          {error && (
            <div
              style={{
                background: '#fee2e2',
                color: '#dc2626',
                padding: '12px 16px',
                borderRadius: 10,
                marginBottom: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
              }}
            >
              <AlertTriangle size={16} /> {error}
            </div>
          )}

          {/* Stats row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 14,
              marginBottom: 36,
            }}
          >
            <MetricCard
              icon={<Building2 size={22} />}
              value={properties.length}
              label="Total Properties"
              accent={BRAND_GREEN}
              accentBg="#f0faf9"
            />
            <MetricCard
              icon={<ClipboardList size={22} />}
              value={activeProjects.length}
              label="Active Projects"
              accent="#2563eb"
              accentBg="#eff6ff"
            />
            <MetricCard
              icon={<Clock size={22} />}
              value={awaitingBids.length}
              label="Awaiting Bids"
              accent="#d97706"
              accentBg="#fef3c7"
            />
            <MetricCard
              icon={<CheckCircle2 size={22} />}
              value={completedThisMonth.length}
              label="Completed This Month"
              accent="#16a34a"
              accentBg="#dcfce7"
            />
            <MetricCard
              icon={<DollarSign size={22} />}
              value={committedSpend > 0 ? formatCurrency(committedSpend) : '—'}
              label="Committed Spend"
              accent="#7c3aed"
              accentBg="#ede9fe"
            />
            <MetricCard
              icon={<TrendingUp size={22} />}
              value={completedSpend > 0 ? formatCurrency(completedSpend) : '—'}
              label="Completed Spend"
              accent="#0891b2"
              accentBg="#e0f2fe"
            />
          </div>

          {/* CB:BEGIN pm-channel-needs-attention-section */}
          {needsAttention.length > 0 && (
            <div
              style={{
                background: '#fffbeb',
                border: '1.5px solid #fbbf24',
                borderRadius: 16,
                padding: '20px 24px',
                marginBottom: 32,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <AlertTriangle size={18} color="#d97706" />
                <span style={{ fontSize: 15, fontWeight: 700, color: '#92400e' }}>
                  Needs Attention ({needsAttention.length})
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {needsAttention.map((p) => {
                  const isDraft = p.status === 'DRAFT';
                  const isOverdue = !isDraft && p.requestedResponseBy && new Date(p.requestedResponseBy) < now;
                  const propertyLabel = p.property?.label || p.property?.address1 || p.address;
                  return (
                    <div
                      key={p.id}
                      style={{
                        background: '#fff',
                        border: `1px solid ${isDraft ? '#fbbf24' : '#fde68a'}`,
                        borderRadius: 10,
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                          {propertyLabel} — {p.projectType.charAt(0) + p.projectType.slice(1).toLowerCase()}
                        </div>
                        <div style={{ fontSize: 12, color: isDraft ? '#92400e' : '#92400e', marginTop: 3 }}>
                          {isDraft
                            ? 'Signature required before contractors are notified'
                            : isOverdue
                            ? `Response deadline passed (${formatDate(p.requestedResponseBy)})`
                            : `No bids after ${Math.floor((now.getTime() - new Date(p.createdAt).getTime()) / (24 * 60 * 60 * 1000))} days`}
                        </div>
                      </div>
                      {isDraft ? (
                        <button
                          onClick={() => setAcknowledgeProject(p)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Sign & Activate
                        </button>
                      ) : (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            background: isOverdue ? '#fee2e2' : '#fef3c7',
                            color: isOverdue ? '#dc2626' : '#b45309',
                            padding: '3px 10px',
                            borderRadius: 20,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isOverdue ? 'Overdue' : 'No Bids Yet'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* CB:END pm-channel-needs-attention-section */}

          {/* Two-column layout: projects table + properties panel */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 340px',
              gap: 24,
              alignItems: 'start',
            }}
          >
            {/* Projects table */}
            <div id="pm-projects-section">
              <div
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px',
                    borderBottom: '1px solid #f1f5f9',
                    flexWrap: 'wrap',
                    gap: 12,
                  }}
                >
                  <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                    Projects ({filteredProjects.length})
                  </h2>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* Filters */}
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        fontSize: 13,
                        background: '#fff',
                        color: '#374151',
                      }}
                    >
                      <option value="">All Statuses</option>
                      {['DRAFT', 'POSTED', 'BIDS_COLLECTING', 'BIDS_READY', 'DECIDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'].map(
                        (s) => (
                          <option key={s} value={s}>
                            {statusLabel(s)}
                          </option>
                        )
                      )}
                    </select>
                    <select
                      value={filterPropertyId}
                      onChange={(e) => setFilterPropertyId(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        fontSize: 13,
                        background: '#fff',
                        color: '#374151',
                      }}
                    >
                      <option value="">All Properties</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label || p.address1}
                        </option>
                      ))}
                    </select>
                    <select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        fontSize: 13,
                        background: '#fff',
                        color: '#374151',
                      }}
                    >
                      <option value="">All Priorities</option>
                      <option value="EMERGENCY">Emergency</option>
                      <option value="HIGH">High</option>
                      <option value="NORMAL">Normal</option>
                    </select>
                    <button
                      onClick={() => setShowAddProject(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 14px',
                        background: BRAND_GREEN,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <Plus size={14} /> New Project
                    </button>
                  </div>
                </div>

                {/* CB:BEGIN pm-channel-empty-state */}
                {filteredProjects.length === 0 ? (
                  <div
                    style={{
                      padding: '52px 32px',
                      textAlign: 'center',
                      maxWidth: 480,
                      margin: '0 auto',
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-flex',
                        padding: 16,
                        background: '#e6f5ef',
                        borderRadius: '50%',
                        marginBottom: 20,
                      }}
                    >
                      <ClipboardList size={32} style={{ color: BRAND_GREEN }} />
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                      {properties.length === 0 ? 'Start your first project' : 'No projects yet'}
                    </div>
                    <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, marginBottom: 24 }}>
                      {properties.length === 0
                        ? 'Add a property to your portfolio, then submit a repair or replacement project to receive contractor bids.'
                        : 'Submit a repair or replacement project to start receiving contractor bids. No phone calls required.'}
                    </div>
                    <button
                      onClick={() =>
                        properties.length === 0 ? setShowAddProperty(true) : setShowAddProject(true)
                      }
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '12px 24px',
                        background: BRAND_GREEN,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 15,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      <Plus size={16} />
                      {properties.length === 0 ? 'Add First Property' : 'Create First Project'}
                    </button>
                    {properties.length > 0 && (
                      <p style={{ marginTop: 14, fontSize: 12, color: '#94a3b8' }}>
                        Contractors in your area will see and bid on your project within 24–72 hours.
                      </p>
                    )}
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                          {['Property', 'Type', 'Priority', 'Status', 'Bids', 'Response By', 'Created'].map(
                            (col) => (
                              <th
                                key={col}
                                style={{
                                  padding: '10px 16px',
                                  textAlign: 'left',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: '#6b7280',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {col}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProjects.map((p) => {
                          const pc = PRIORITY_COLORS[p.priority] || PRIORITY_COLORS.NORMAL;
                          return (
                            <tr
                              key={p.id}
                              style={{
                                borderBottom: '1px solid #f3f4f6',
                                transition: 'background 0.12s',
                              }}
                              onMouseEnter={(e) =>
                                ((e.currentTarget as HTMLTableRowElement).style.background = '#f8fafc')
                              }
                              onMouseLeave={(e) =>
                                ((e.currentTarget as HTMLTableRowElement).style.background = '')
                              }
                            >
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                                  {p.property?.label || p.property?.address1 || p.address}
                                </div>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                  {p.property ? `${p.property.city}, ${p.property.state}` : `${p.city || ''}, ${p.state || ''}`}
                                </div>
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>
                                {p.projectType.charAt(0) + p.projectType.slice(1).toLowerCase()}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                <StatusBadge status={p.priority} label={pc.label} />
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {p.status === 'DRAFT' ? (
                                  <button
                                    onClick={() => setAcknowledgeProject(p)}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                  >
                                    Sign & Activate
                                  </button>
                                ) : (
                                  <StatusBadge status={p.status} label={statusLabel(p.status)} />
                                )}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151', textAlign: 'center' }}>
                                {p.bids?.length ?? 0}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: 12, color: p.requestedResponseBy ? '#374151' : '#94a3b8', whiteSpace: 'nowrap' }}>
                                {formatDate(p.requestedResponseBy)}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                {formatDate(p.createdAt)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Properties panel */}
            <div id="pm-properties-section">
              <div
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px',
                    borderBottom: '1px solid #f1f5f9',
                  }}
                >
                  <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                    Properties ({properties.length})
                  </h2>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => setShowImportProperties(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      ↑ Import
                    </button>
                    <button
                      onClick={() => setShowAddProperty(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                </div>

                {properties.length === 0 ? (
                  <div style={{ padding: '40px 24px', textAlign: 'center', color: '#94a3b8' }}>
                    <Building2 size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No properties yet</div>
                    <div style={{ fontSize: 13 }}>Add your first property to get started.</div>
                  </div>
                ) : (
                  <PmPropertiesList properties={properties} onNavigate={onNavigate} />
                )}
              </div>
            </div>
          </div>

        </ContentLimiter>
      </main>

      {/* Modals */}
      {showAddProperty && (
        <AddPropertyModal
          onClose={() => setShowAddProperty(false)}
          onSaved={fetchData}
          token={token}
        />
      )}
      {showImportProperties && (
        <ImportPropertiesModal
          onClose={() => setShowImportProperties(false)}
          onImported={fetchData}
          token={token}
        />
      )}
      {showAddProject && (
        <AddProjectModal
          properties={properties}
          onClose={() => setShowAddProject(false)}
          onSaved={fetchData}
          token={token}
        />
      )}
      {acknowledgeProject && (
        <AcknowledgeProjectModal
          project={acknowledgeProject}
          onClose={() => setAcknowledgeProject(null)}
          onAcknowledged={fetchData}
          token={token}
        />
      )}
    </div>
  );
};

export default PropertyManagerDashboard;
// CB:END pm-channel-dashboard
